import { ref, type Ref } from 'vue'

export const APP_TIMEZONE_STORAGE_KEY = 'hmAppTimezone'

export const APP_TIMEZONES = [
  { id: 'Asia/Shanghai', label: '中国时区', detail: 'Asia/Shanghai（UTC+8）' },
  { id: 'America/Toronto', label: '加拿大东部时区', detail: 'America/Toronto（含夏令时）' },
] as const

export type AppTimeZoneId = (typeof APP_TIMEZONES)[number]['id']

export interface ZonedNowParts {
  year: number
  /** 1–12 */
  month: number
  day: number
  /** 当天 0 点起的分钟数 */
  minutes: number
}

const DEFAULT_TIME_ZONE: AppTimeZoneId = 'Asia/Shanghai'

function isAppTimeZoneId(value: string): value is AppTimeZoneId {
  return APP_TIMEZONES.some((zone) => zone.id === value)
}

function readStoredTimeZone(): AppTimeZoneId {
  try {
    const raw = localStorage.getItem(APP_TIMEZONE_STORAGE_KEY)
    if (raw && isAppTimeZoneId(raw)) return raw
  } catch {
    /* 隐私模式等无法读 localStorage 时用默认时区 */
  }
  return DEFAULT_TIME_ZONE
}

export const appTimeZone: Ref<AppTimeZoneId> = ref(readStoredTimeZone())

export function setAppTimeZone(id: AppTimeZoneId): void {
  appTimeZone.value = id
  try {
    localStorage.setItem(APP_TIMEZONE_STORAGE_KEY, id)
  } catch {
    /* 写入失败时仍保留本次会话内的选择 */
  }
}

function formatPart(parts: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes): string {
  return parts.find((part) => part.type === type)?.value ?? ''
}

/** 把某一时刻换成指定 IANA 时区的公历日期和当天分钟数。 */
export function zonedNowParts(timeZone: string, instant: Date = new Date()): ZonedNowParts {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(instant)
  const year = Number(formatPart(parts, 'year'))
  const month = Number(formatPart(parts, 'month'))
  const day = Number(formatPart(parts, 'day'))
  let hour = Number(formatPart(parts, 'hour'))
  const minute = Number(formatPart(parts, 'minute'))
  if (hour === 24) hour = 0
  return { year, month, day, minutes: hour * 60 + minute }
}

export function currentZonedNow(instant: Date = new Date()): ZonedNowParts {
  return zonedNowParts(appTimeZone.value, instant)
}
